import React from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getContrastRatio } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_SALON_BUTTON_STYLE,
  SALON_PUBLIC_LAYOUT_REGISTRY,
} from '../../../theme/salonPublicLayout';
import {
  DEFAULT_SALON_PUBLIC_THEME,
  SALON_FONT_CATALOG,
  buildSalonPublicTheme,
} from '../../../theme/salonPublicTheme';
import SalonButtonStyleSample from './SalonButtonStyleSample';
import SalonEffectiveColorControl from './SalonEffectiveColorControl';
import SalonLayoutOptionGroup from './SalonLayoutOptionGroup';


const COLOR_FIELDS = [
  'background_color',
  'border_color',
  'text_color',
];

const SIZE_LABELS = Object.freeze({
  xs: '12',
  sm: '14',
  md: '16',
  lg: '24',
  xl: '32',
});

const firstError = (error) => (Array.isArray(error) ? error[0] : error || '');

const SalonCompactSelectField = ({
  field,
  label,
  value,
  options,
  errors,
  onChange,
  disabled,
  registerFieldRef,
  hideVisibleLabel = false,
}) => {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      {!hideVisibleLabel && (
        <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'start' }}>
          {label}
        </Typography>
      )}
      <FormControl
        size="small"
        fullWidth
        disabled={disabled}
        error={Boolean(firstError(errors?.[field]))}
      >
        <Select
          native
          value={value}
          onChange={(event) => onChange(field, event.target.value)}
          inputRef={(element) => registerFieldRef(field, element)}
          inputProps={{ 'aria-label': label }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {firstError(errors?.[field]) && (
          <FormHelperText role="alert">
            {firstError(errors[field])}
          </FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
};

const SalonButtonLayoutControls = ({
  value = DEFAULT_SALON_BUTTON_STYLE,
  errors = {},
  onChange,
  disabled = false,
  registerFieldRef = () => {},
  theme,
}) => {
  const { t, i18n } = useTranslation();
  const buttonValue = { ...DEFAULT_SALON_BUTTON_STYLE, ...value };
  const builtTheme = buildSalonPublicTheme(
    theme || DEFAULT_SALON_PUBLIC_THEME,
  );
  const resolvedTheme = builtTheme.tokens.salon;
  const inheritedColors = {
    background_color: resolvedTheme.primary,
    border_color: resolvedTheme.primary,
    text_color: resolvedTheme.onPrimary,
  };
  const effectiveFontKey = builtTheme.config.font_key;
  const buttonRoleLabel = t('salon_theme.styles.button_sample');
  const accessibleLabel = (field) => (
    `${t(`salon_theme.styles.${field}`)} — ${buttonRoleLabel}`
  );
  const isBold = buttonValue.weight >= 700;
  const direction = typeof i18n?.dir === 'function' && i18n.dir() === 'rtl'
    ? 'rtl'
    : 'ltr';
  const hasComparableColors = /^#[0-9A-Fa-f]{6}$/.test(buttonValue.background_color)
    && /^#[0-9A-Fa-f]{6}$/.test(buttonValue.text_color);
  const hasLowContrast = hasComparableColors && getContrastRatio(
    buttonValue.background_color,
    buttonValue.text_color,
  ) < 4.5;

  const fontOptions = [
    {
      value: 'inherit',
      label: SALON_FONT_CATALOG[effectiveFontKey]?.label,
    },
    ...Object.entries(SALON_FONT_CATALOG)
      .filter(([fontKey]) => (
        buttonValue.font_key !== 'inherit' || fontKey !== effectiveFontKey
      ))
      .map(([fontKey, preset]) => ({
        value: fontKey,
        label: preset.label,
      })),
  ];
  const advancedSelectFields = [
    {
      field: 'transform',
      label: t('salon_theme.styles.transform'),
      options: ['none', 'uppercase'].map((option) => ({
        value: option,
        label: t(`salon_theme.styles.options.transform.${option}`),
      })),
    },
    {
      field: 'letter_spacing',
      label: t('salon_theme.styles.letter_spacing'),
      options: ['normal', 'wide'].map((option) => ({
        value: option,
        label: t(`salon_theme.styles.options.letter_spacing.${option}`),
      })),
    },
  ];

  return (
    <Stack spacing={2.5}>
      {Object.entries(SALON_PUBLIC_LAYOUT_REGISTRY.buttons).map(([axis, values]) => (
        <SalonLayoutOptionGroup
          key={axis}
          legend={t(`salon_theme.layout.buttons.${axis}.legend`)}
          name={`salon-layout-buttons-${axis}`}
          value={buttonValue[axis]}
          options={values.map((optionValue) => ({
            value: optionValue,
            label: t(`salon_theme.layout.buttons.${axis}.options.${optionValue}`),
          }))}
          onChange={(nextValue) => onChange(axis, nextValue)}
          disabled={disabled}
          error={firstError(errors?.[axis])}
          focusRef={(element) => registerFieldRef(axis, element)}
          selectedLabel={t('salon_theme.layout.selected')}
          renderThumbnail={(option) => (
            <SalonButtonStyleSample
              buttons={{ ...buttonValue, [axis]: option.value }}
              label={t('salon_theme.styles.button_sample')}
              theme={theme}
              direction={direction}
            />
          )}
        />
      ))}

      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {COLOR_FIELDS.map((field) => (
            <SalonEffectiveColorControl
              key={field}
              label={t(`salon_theme.styles.${field}`)}
              value={buttonValue[field]}
              inheritedColor={inheritedColors[field]}
              error={firstError(errors?.[field])}
              onChange={(nextValue) => onChange(field, nextValue)}
              resetLabel={t('salon_theme.styles.use_theme_color')}
              disabled={disabled}
              inputRef={(element) => registerFieldRef(field, element)}
            />
          ))}
        </Box>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr) 68px auto',
            sm: 'minmax(145px, 1fr) 68px auto',
          },
          gap: 1,
          alignItems: 'start',
        }}
      >
        <SalonCompactSelectField
          field="font_key"
          label={accessibleLabel('font')}
          value={buttonValue.font_key}
          options={fontOptions}
          errors={errors}
          onChange={onChange}
          disabled={disabled}
          registerFieldRef={registerFieldRef}
          hideVisibleLabel
        />
        <SalonCompactSelectField
          field="text_size"
          label={accessibleLabel('text_size')}
          value={buttonValue.text_size}
          options={Object.entries(SIZE_LABELS).map(([option, label]) => ({
            value: option,
            label,
          }))}
          errors={errors}
          onChange={onChange}
          disabled={disabled}
          registerFieldRef={registerFieldRef}
          hideVisibleLabel
        />
        <ToggleButtonGroup size="small" disabled={disabled}>
          <ToggleButton
            value="bold"
            selected={isBold}
            aria-label={accessibleLabel('bold')}
            onClick={() => onChange(
              'weight',
              isBold ? DEFAULT_SALON_BUTTON_STYLE.weight : 700,
            )}
            ref={(element) => registerFieldRef('weight', element)}
            sx={{ minWidth: 40, fontWeight: 800 }}
          >
            {t('salon_theme.styles.bold_short')}
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={Boolean(buttonValue.italic)}
            aria-label={accessibleLabel('italic')}
            onClick={() => onChange('italic', !buttonValue.italic)}
            ref={(element) => registerFieldRef('italic', element)}
            sx={{ minWidth: 40, fontStyle: 'italic', fontFamily: 'serif' }}
          >
            {t('salon_theme.styles.italic_short')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {advancedSelectFields.map((fieldConfig) => (
          <SalonCompactSelectField
            key={fieldConfig.field}
            {...fieldConfig}
            value={buttonValue[fieldConfig.field]}
            errors={errors}
            onChange={onChange}
            disabled={disabled}
            registerFieldRef={registerFieldRef}
          />
        ))}
      </Box>

      {[firstError(errors?.weight), firstError(errors?.italic)]
        .filter(Boolean)
        .map((message) => (
          <FormHelperText key={message} error role="alert">{message}</FormHelperText>
        ))}

      {hasLowContrast && (
        <Alert severity="warning" role="status">
          {t('salon_theme.styles.low_contrast_warning')}
        </Alert>
      )}
    </Stack>
  );
};

export default SalonButtonLayoutControls;
